import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
    Grid,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Chip,
    Pagination,
    Menu,
    IconButton,
    Tooltip
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useStyles from "../../assets/styles/SupMangComp.style";
import API from "../../utils/Axios.utils";

const InterviewTrack = () => {
    const classes = useStyles();

    // Pagination
    const [listCount, setListCount] = useState(0);
    const [page, setPage] = useState(1);

    // Data from API
    const [interviews, setInterviews] = useState([]);
    console.log('interviews :- ', interviews)
    // Job titles from API - fetched ONLY ONCE
    const [jobTitles, setJobTitles] = useState([]);
    const [jobTitlesLoading, setJobTitlesLoading] = useState(true);

    // Core skills from API - fetched when job code changes
    const [coreSkills, setCoreSkills] = useState([]);
    const [coreSkillsLoading, setCoreSkillsLoading] = useState(false);

    // Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);

    // ✅ UPDATED: Added status to filters
    const [filters, setFilters] = useState({
        jobTitle: "",
        coreSkill: "",
        status: "", // NEW ✅
        dateRange: "",
    });

    const [filtersApplied, setFiltersApplied] = useState(false);

    // const dateRanges = ["Today", "This Week", "Next Week"];
    const dateRanges = [
        { label: "Today", value: "today" },
        { label: "This Week", value: "this_week" },
        { label: "Next Week", value: "next_week" }
    ];

    // ✅ NEW: Status options with proper camelCase labels
    const statusOptions = [
        // "Submitted",
        // "Reviewed",
        // "Rejected",
        // "Short_listed",
        "Interview_Scheduled",
        "Interview_Cancelled",
        "Staffed",
        // "Position_on_hold"
    ];

    // Memoize token and config
    const token = useMemo(() =>
        JSON.parse(localStorage.getItem("userLoggedIn") || "{}").token || "",
        []
    );
    const config = useMemo(() => ({
        headers: { Authorization: `Bearer ${token}` },
    }), [token]);

    // 1. Fetch job titles ONLY ONCE on mount
    const fetchJobTitles = useCallback(async () => {
        try {
            setJobTitlesLoading(true);
            const res = await API.get("api/job/all-job-title", config);
            setJobTitles(res?.data?.jobs || []);
        } catch (error) {
            console.error("Error fetching job titles:", error);
            setJobTitles([]);
        } finally {
            setJobTitlesLoading(false);
        }
    }, [config]);

    // 2. Fetch core skills when job code changes
    const fetchCoreSkills = useCallback(async (jobCode) => {
        if (!jobCode) {
            setCoreSkills([]);
            setFilters(prev => ({ ...prev, coreSkill: "" }));
            return;
        }

        try {
            setCoreSkillsLoading(true);
            const res = await API.get(`api/job/coreskills/${jobCode}`, config);
            const skills = (res?.data?.coreSkills || []).map(skill => skill.name);
            setCoreSkills(skills);

            if (!skills.includes(filters.coreSkill)) {
                setFilters(prev => ({ ...prev, coreSkill: "" }));
            }
        } catch (error) {
            console.error("Error fetching core skills:", error);
            setCoreSkills([]);
            setFilters(prev => ({ ...prev, coreSkill: "" }));
        } finally {
            setCoreSkillsLoading(false);
        }
    }, [config, filters.coreSkill]);

    // Fetch job titles on mount
    useEffect(() => {
        fetchJobTitles();
    }, []);

    // Fetch core skills when jobTitle filter changes
    useEffect(() => {
        fetchCoreSkills(filters.jobTitle);
    }, [filters.jobTitle, fetchCoreSkills]);

    // ✅ UPDATED: Added status param
    const buildQueryParams = useCallback((currentPage = page) => {
        const params = new URLSearchParams({
            page: currentPage.toString(),
            pageSize: "10",
        });
        if (filters.jobTitle) params.append("jobTitle", filters.jobTitle);
        if (filters.coreSkill) params.append("coreSkill", filters.coreSkill);
        if (filters.status) params.append("status", filters.status); // NEW ✅
        if (filters.dateRange) params.append("dateRange", filters.dateRange);
        return params.toString();
    }, [filters, page]);

    // 3. Fetch profiles - NO AUTO TRIGGER
    const getProfiles = useCallback(async (queryString) => {
        if (!queryString) return;
        try {
            const url = `/api/profile/all_candidate_profile?${queryString}`;
            const res = await API.get(url, config);

            const candidates = res?.data?.candidates || [];
            const jobCodeCoreSkills = res?.data?.jobCodeCoreSkills || [];
            setListCount(res?.data?.totalCount || 0);

            const jobCodeToSkills = jobCodeCoreSkills.reduce((acc, item) => {
                const skillNames = (item.coreSkills || []).map(cs =>
                    typeof cs === 'object' ? cs.name : cs
                );
                acc[item.job_code] = skillNames;
                return acc;
            }, {});

            const mapped = candidates.map((c) => {
                const interviewDate = c.interview_datetime
                    ? new Date(c.interview_datetime).toISOString().substring(0, 10)
                    : "";
                const jobCode = c.job_code;
                let coreSkills = [];

                if (c.coreSkillName) {
                    coreSkills = [c.coreSkillName];
                } else if (jobCode && jobCodeToSkills[jobCode]) {
                    coreSkills = jobCodeToSkills[jobCode];
                }

                return {
                    id: c.profile_id,
                    candidateName: c.candidate_name,
                    jobTitle: jobCode,
                    coreSkills,
                    interviewDate,
                    status: c.candidate_status || "Submitted",
                    resumePath: c.profile_path,
                    supplier: c.supplier?.supplier_org_name || "-",
                    customer: c.customer?.customer_name || "-",
                    status_group: c.status_group || '-',
                    detailed_status: c.detailed_status || '-'
                };
            });
            setInterviews(mapped);
        } catch (error) {
            console.error("Error fetching candidate profiles:", error);
            setInterviews([]);
            setListCount(0);
        }
    }, [config]);

    // Initial load ONLY on mount
    useEffect(() => {
        getProfiles(buildQueryParams());
    }, []);

    const handlePageChange = (e, value) => {
        setPage(value);
        getProfiles(buildQueryParams(value));
    };

    const handleMenuOpen = (event, rowId) => {
        setAnchorEl(event.currentTarget);
        setMenuRowId(rowId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    // const handleFilterChange = (key, value) => {
    //     setFilters((prev) => ({ ...prev, [key]: value }));
    // };
    const handleFilterChange = useCallback((key, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [key]: value };

            // Filter reset hierarchy:
            // 1. JobCode change → reset coreSkill, status, dateRange
            if (key === 'jobTitle' && value !== prev.jobTitle) {
                newFilters.coreSkill = '';
                newFilters.status = '';
                newFilters.dateRange = '';
                // Clear core skills when job title changes
                setCoreSkills([]);
            }

            // 2. CoreSkill change → reset status, dateRange  
            if (key === 'coreSkill' && value !== prev.coreSkill) {
                newFilters.status = '';
                newFilters.dateRange = '';
            }

            // 3. Status change → reset dateRange (only if not InterviewScheduled)
            if (key === 'status' && value !== prev.status) {
                if (value !== 'InterviewScheduled') {
                    newFilters.dateRange = '';
                }
            }

            return newFilters;
        });
        setPage(1);
    }, []);


    const handleSubmitFilters = () => {
        setFiltersApplied(true);
        setPage(1);
        getProfiles(buildQueryParams(1));
    };

    const handleClearFilters = () => {
        setFilters({ jobTitle: "", coreSkill: "", status: "", dateRange: "" }); // UPDATED ✅
        setCoreSkills([]);
        setFiltersApplied(false);
        setPage(1);

        const clearedParams = new URLSearchParams({
            page: "1",
            pageSize: "10"
        }).toString();

        getProfiles(clearedParams);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Interview_Cancelled":
                return "error";
            case "Confirmed":
                return "success";
            case "In Progress":
                return "info";
            case "Pending":
                return "default";
            case "Interview_Scheduled":
                return "info";
            case "Staffed":
                return "success";
            default:
                return "default";
        }
    };
    const showDateRange = filters.status === 'Interview_Scheduled';

    const parseStatusLabel = (status) => {
        if (!status) return "";
        return status
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };
    return (
        <Grid container style={{ padding: 20 }}>
            {/* Header */}
            <Grid item xs={12} style={{ marginBottom: 0 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item>
                        <h2 className={classes.pageTitle}>Interviews Tracking</h2>
                    </Grid>
                </Stack>
            </Grid>

            {/* Filters */}
            <Grid item xs={12} style={{ marginBottom: 12 }}>
                {/* <Paper sx={{ p: 2 }}> */}
                <Grid container spacing={2} alignItems="center">
                    {/* Job Code */}
                    <Grid item xs={12} md={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Job Title</InputLabel>
                            <Select
                                value={filters.jobTitle}
                                label="Job Title"
                                onChange={(e) =>
                                    handleFilterChange("jobTitle", e.target.value)
                                }
                                disabled={jobTitlesLoading}
                            >
                                {jobTitles.map((job) => (
                                    <MenuItem key={job.job_code} value={job.job_code}>
                                        {job.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Core Skill */}
                    <Grid item xs={12} md={2.4}>
                        <FormControl fullWidth size="small" disabled={!filters.jobTitle}>
                            <InputLabel>Core Skill</InputLabel>
                            <Select
                                value={filters.coreSkill}
                                label="Core Skill"
                                onChange={(e) =>
                                    handleFilterChange("coreSkill", e.target.value)
                                }
                                disabled={coreSkillsLoading || !filters.jobTitle}
                            >
                                {coreSkills.map((skill) => (
                                    <MenuItem key={skill} value={skill}>
                                        {skill}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* ✅ NEW: Status Filter */}
                    <Grid item xs={12} md={2.4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) =>
                                    handleFilterChange("status", e.target.value)
                                }
                            >
                                {statusOptions.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        {status}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {showDateRange && (
                        <Grid item xs={12} md={2.4}>
                            <FormControl fullWidth size="small" >
                                <InputLabel>Date Range *</InputLabel>
                                <Select
                                    value={filters.dateRange}
                                    label="Date Range *"
                                    onChange={(e) =>
                                        handleFilterChange("dateRange", e.target.value)
                                    }
                                >
                                    {dateRanges.map((dateRange) => (
                                        <MenuItem key={dateRange.value} value={dateRange.value}>
                                            {dateRange.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}

                    {/* Buttons */}
                    <Grid item xs={12} md={2.4}>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleSubmitFilters}
                                disabled={jobTitlesLoading || coreSkillsLoading}
                            >
                                Submit
                            </Button>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClearFilters}
                                disabled={jobTitlesLoading || coreSkillsLoading}
                            >
                                Clear Filters
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
                {/* </Paper> */}
            </Grid>

            {/* Table */}
            <Grid item xs={12} className={classes.tableGrid}>
                <TableContainer component={Paper} elevation={0}>
                    <Table sx={{ minWidth: 650 }} size="small">
                        <TableHead>
                            <TableRow className={classes.tableHeadRow}>
                                <TableCell className={classes.tableHeadCell}>
                                    Candidate Name
                                </TableCell>
                                <TableCell className={classes.tableHeadCell}>
                                    Job Code
                                </TableCell>
                                <TableCell className={classes.tableHeadCell}>
                                    Core Skills
                                </TableCell>
                                <TableCell className={classes.tableHeadCell}>Status</TableCell>
                                {showDateRange && (
                                    <TableCell className={classes.tableHeadCell}>
                                        Interview Date
                                    </TableCell>
                                )}
                                <TableCell className={classes.tableHeadCell}>
                                    Customer
                                </TableCell>
                                <TableCell className={classes.tableHeadCell}>
                                    Supplier
                                </TableCell>
                                {/* <TableCell align="right" className={classes.tableHeadCell}>
                                    Actions
                                </TableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {interviews.map((row) => (
                                <TableRow key={row.id} hover>
                                    <TableCell className={classes.tableBodyCell}>
                                        {row.candidateName}
                                    </TableCell>
                                    {/* <TableCell className={classes.tableBodyCell}>
                                        {row.jobTitle}
                                    </TableCell> */}
                                    <TableCell className={classes.tableBodyCell}>
                                        <Tooltip
                                            title={
                                                row.jobTitle
                                                    ? jobTitles.find(job => job.job_code === row.jobTitle)?.title || "No title available"
                                                    : "No job title"
                                            }
                                            arrow
                                            placement="top"
                                            enterDelay={500}
                                            leaveDelay={200}
                                        >
                                            <span style={{ cursor: 'pointer', display: 'inline-block' }}>
                                                {row.jobTitle || '-'}
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className={classes.tableBodyCell}>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                            {(row.coreSkills || []).map((skill) => (
                                                <Chip
                                                    key={skill}
                                                    label={skill}
                                                    size="small"
                                                    color="primary"
                                                />
                                            ))}
                                        </Stack>
                                    </TableCell>
                                    <TableCell className={classes.tableBodyCell}>
                                        <Tooltip
                                            title={parseStatusLabel(row?.detailed_status) || 'No detailed status'}
                                            arrow
                                            placement="top"
                                        >
                                            <Chip
                                                label={parseStatusLabel(row?.detailed_status)}
                                                color={getStatusColor(row.status_group)}
                                                size="small"
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    {showDateRange && (
                                        <TableCell className={classes.tableBodyCell}>
                                            {row.interviewDate || "-"}
                                        </TableCell>
                                    )}
                                    <TableCell className={classes.tableBodyCell}>
                                        {row?.customer || '-'}
                                    </TableCell>
                                    <TableCell className={classes.tableBodyCell}>
                                        {row.supplier || '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {interviews.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        {filtersApplied
                                            ? "No interviews found for applied filters."
                                            : "Loading interviews..."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Stack
                    spacing={2}
                    sx={{
                        paddingTop: 2,
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "flex-end",
                    }}
                >
                    <Pagination
                        classes={{ root: classes.pagination }}
                        count={Math.ceil(listCount / 10) || 1}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                    />
                </Stack>
            </Grid>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
            >
                <MenuItem onClick={() => {
                    console.log("View details:", menuRowId);
                    handleMenuClose();
                }}>
                    View Details
                </MenuItem>
                <MenuItem onClick={() => {
                    console.log("Reschedule:", menuRowId);
                    handleMenuClose();
                }}>
                    Reschedule
                </MenuItem>
            </Menu>
        </Grid>
    );
};

export default InterviewTrack;

